<template><div><component-combinator component-name="DtSplitButton" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">This dual-functionality allows for surfacing variations of the default action. It conserves space in the interface and reduces cognitive load. While versatile, they should be used judiciously to avoid overwhelming users or cluttering the UI.</p>
<p class="d-docsite--paragraph">In addition to the <RouteLink to="/components/button.html" class="d-docsite--link d-link">Button component's</RouteLink> documentation:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The default button supports text or icons, while the secondary action is always an icon.</li>
<li class="d-docsite--list-element">The secondary action selected from its menu replaces the primary action.</li>
<li class="d-docsite--list-element">Ensure that primary and secondary actions are clearly labeled to avoid user confusion.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use Split Buttons when you need to offer a default action paired with closely related actions, such as &quot;Save&quot; with secondary actions like &quot;Save as Draft&quot; or &quot;Save all.&quot;</li>
<li class="d-docsite--list-element">Reserve for scenarios where multiple related actions can reasonably be grouped within the same context via the secondary action.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid using if the secondary action is unrelated or distantly related.</li>
<li class="d-docsite--list-element">Refrain from grouping multiple Split Buttons together, as this can lead to a cluttered and confusing interface.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;clear&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
  <dt-split-button importance="outlined" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
  <dt-split-button importance="clear" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
</dt-stack>
</code-example>
<h3 id="critical" tabindex="-1" class="d-docsite--header-3">Critical <a class="header-anchor d-link d-docsite--link d-link" href="#critical" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button kind=&quot;critical&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; kind=&quot;critical&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;clear&quot; kind=&quot;critical&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button kind="critical" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
  <dt-split-button importance="outlined" kind="critical" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
  <dt-split-button importance="clear" kind="critical" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
</dt-stack>
</code-example>
<h3 id="positive" tabindex="-1" class="d-docsite--header-3">Positive <a class="header-anchor d-link d-docsite--link d-link" href="#positive" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button kind=&quot;positive&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place Call &lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; kind=&quot;positive&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place Call &lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;clear&quot; kind=&quot;positive&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place Call &lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
    <dt-split-button kind="positive" end-tooltip-text="More calling options"> Place Call </dt-split-button>
    <dt-split-button importance="outlined" kind="positive" end-tooltip-text="More calling options"> Place Call </dt-split-button>
    <dt-split-button importance="clear" kind="positive" end-tooltip-text="More calling options"> Place Call </dt-split-button>
</dt-stack>
</code-example>
<h3 id="muted" tabindex="-1" class="d-docsite--header-3">Muted <a class="header-anchor d-link d-docsite--link d-link" href="#muted" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button importance=&quot;outlined&quot; kind=&quot;muted&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place Call &lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;clear&quot; kind=&quot;muted&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place Call &lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
    <dt-split-button importance="outlined" kind="muted" end-tooltip-text="More calling options"> Place Call </dt-split-button>
    <dt-split-button importance="clear" kind="muted" end-tooltip-text="More calling options"> Place Call </dt-split-button>
</dt-stack>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>disabled</code> prop to disable both buttons, or use <code v-pre>start-disabled</code> and <code v-pre>end-disabled</code> to disable each button independently.</p>
<code-example source-code='&lt;dt-split-button disabled end-tooltip-text=&quot;More calling options&quot;&gt; Both disabled &lt;/dt-split-button&gt;
&lt;dt-split-button start-disabled end-tooltip-text=&quot;More calling options&quot;&gt; Start disabled &lt;/dt-split-button&gt;
&lt;dt-split-button end-disabled end-tooltip-text=&quot;More calling options&quot;&gt; End disabled &lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
    <dt-split-button disabled end-tooltip-text="More calling options"> Both disabled </dt-split-button>
    <dt-split-button start-disabled end-tooltip-text="More calling options"> Start disabled </dt-split-button>
    <dt-split-button end-disabled end-tooltip-text="More calling options"> End disabled </dt-split-button>
</dt-stack>
</code-example>
<h3 id="active" tabindex="-1" class="d-docsite--header-3">Active <a class="header-anchor d-link d-docsite--link d-link" href="#active" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button start-active end-tooltip-text=&quot;More calling options&quot;&gt; Start active &lt;/dt-split-button&gt;
&lt;dt-split-button end-active end-tooltip-text=&quot;More calling options&quot;&gt; End active &lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button start-active end-tooltip-text="More calling options"> Start active </dt-split-button>
  <dt-split-button end-active end-tooltip-text="More calling options"> End active </dt-split-button>
</dt-stack>
</code-example>
<h3 id="no-divider" tabindex="-1" class="d-docsite--header-3">No Divider <a class="header-anchor d-link d-docsite--link d-link" href="#no-divider" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>:show-divider=&quot;false&quot;</code> to hide the vertical divider between the start and end buttons. This is only available for the <code v-pre>clear</code> importance variant.</p>
<code-example source-code='&lt;dt-split-button importance=&quot;clear&quot; kind=&quot;muted&quot; :show-divider=&quot;false&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;clear&quot; :show-divider=&quot;false&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  Place Call
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 3 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button importance="clear" kind="muted" :show-divider="false" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
  <dt-split-button importance="clear" :show-divider="false" end-tooltip-text="More calling options">
    Place Call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
</dt-stack>
</code-example>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-split-button :size=&quot;100&quot; end-tooltip-text=&quot;More calling options&quot;&gt; 100 &lt;/dt-split-button&gt;
&lt;dt-split-button :size=&quot;200&quot; end-tooltip-text=&quot;More calling options&quot;&gt; 200 &lt;/dt-split-button&gt;
&lt;dt-split-button :size=&quot;300&quot; end-tooltip-text=&quot;More calling options&quot;&gt; 300 &lt;/dt-split-button&gt;
&lt;dt-split-button :size=&quot;400&quot; end-tooltip-text=&quot;More calling options&quot;&gt; 400 &lt;/dt-split-button&gt;
&lt;dt-split-button :size=&quot;500&quot; end-tooltip-text=&quot;More calling options&quot;&gt; 500 &lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button :size="100" end-tooltip-text="More calling options"> 100 </dt-split-button>
  <dt-split-button :size="200" end-tooltip-text="More calling options"> 200 </dt-split-button>
  <dt-split-button :size="300" end-tooltip-text="More calling options"> 300 </dt-split-button>
  <dt-split-button :size="400" end-tooltip-text="More calling options"> 400 </dt-split-button>
  <dt-split-button :size="500" end-tooltip-text="More calling options"> 500 </dt-split-button>
</dt-stack>
</code-example>
<h2 id="loading" tabindex="-1" class="d-docsite--header-2">Loading <a class="header-anchor d-link d-docsite--link d-link" href="#loading" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-split-button start-loading end-tooltip-text=&quot;More calling options&quot;&gt; Place call &lt;/dt-split-button&gt;
&lt;dt-split-button start-loading importance=&quot;outlined&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place call &lt;/dt-split-button&gt;
&lt;dt-split-button start-loading importance=&quot;clear&quot; end-tooltip-text=&quot;More calling options&quot;&gt; Place call &lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button start-loading end-tooltip-text="More calling options"> Place call </dt-split-button>
  <dt-split-button start-loading importance="outlined" end-tooltip-text="More calling options"> Place call </dt-split-button>
  <dt-split-button start-loading importance="clear" end-tooltip-text="More calling options"> Place call </dt-split-button>
</dt-stack>
</code-example>
<h2 id="navigation" tabindex="-1" class="d-docsite--header-2">Navigation <a class="header-anchor d-link d-docsite--link d-link" href="#navigation" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The start button supports navigation via <code v-pre>start-href</code> (renders as <code v-pre>&lt;a&gt;</code>) or <code v-pre>start-to</code> (renders as <code v-pre>&lt;router-link&gt;</code>). Navigation only applies to the start button — the end button is a dropdown trigger.</p>
<h3 id="external-link" tabindex="-1" class="d-docsite--header-3">External Link <a class="header-anchor d-link d-docsite--link d-link" href="#external-link" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button
  start-href=&quot;https://dialpad.com&quot;
  start-target=&quot;_blank&quot;
  start-rel=&quot;noopener noreferrer&quot;
  end-tooltip-text=&quot;More options&quot;
&gt;
  Visit Dialpad
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-split-button
  start-href="https://dialpad.com"
  start-target="_blank"
  start-rel="noopener noreferrer"
  end-tooltip-text="More options"
>
  Visit Dialpad
  <template #dropdownList>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
  </template>
</dt-split-button>
</code-example>
<h3 id="router-link" tabindex="-1" class="d-docsite--header-3">Router Link <a class="header-anchor d-link d-docsite--link d-link" href="#router-link" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button
  start-to=&quot;/components/button&quot;
  end-tooltip-text=&quot;More options&quot;
&gt;
  Go to DtButton docs
  &lt;template #dropdownList&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 1 &lt;/dt-list-item&gt;
    &lt;dt-list-item role=&quot;menuitem&quot; navigation-type=&quot;arrow-keys&quot;&gt; Option 2 &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-split-button
  start-to="/components/button"
  end-tooltip-text="More options"
>
  Go to DtButton docs
  <template #dropdownList>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
  </template>
</dt-split-button>
</code-example>
<h2 id="icon-support" tabindex="-1" class="d-docsite--header-2">Icon Support <a class="header-anchor d-link d-docsite--link d-link" href="#icon-support" aria-hidden="true">#</a></h2>
<h3 id="icon-and-label" tabindex="-1" class="d-docsite--header-3">Icon and Label <a class="header-anchor d-link d-docsite--link d-link" href="#icon-and-label" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button importance=&quot;outlined&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  Place call
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; start-icon-position=&quot;blockStart&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  Place call
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; start-icon-position=&quot;end&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  Place call
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; start-icon-position=&quot;blockEnd&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  Place call
&lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100" class="d-fw-wrap">
  <dt-split-button importance="outlined" end-tooltip-text="More calling options">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
    Place call
  </dt-split-button>
  <dt-split-button importance="outlined" start-icon-position="blockStart" end-tooltip-text="More calling options">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
    Place call
  </dt-split-button>
  <dt-split-button importance="outlined" start-icon-position="end" end-tooltip-text="More calling options">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
    Place call
  </dt-split-button>
  <dt-split-button importance="outlined" start-icon-position="blockEnd" end-tooltip-text="More calling options">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
    Place call
  </dt-split-button>
</dt-stack>
</code-example>
<h3 id="dual-icons-on-start-button" tabindex="-1" class="d-docsite--header-3">Dual Icons on Start Button <a class="header-anchor d-link d-docsite--link d-link" href="#dual-icons-on-start-button" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>#startIcon</code> and <code v-pre>#startEndIcon</code> together to place icons at both the start and end positions
within the start button. This uses the same dual-icon pattern as <a href="/components/button.html#start-and-end-icons" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtButton</a>.</p>
<code-example source-code='&lt;dt-split-button importance=&quot;outlined&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  &lt;template #startEndIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;arrow-down&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  Place call
&lt;/dt-split-button&gt;'>
<dt-split-button importance="outlined" end-tooltip-text="More calling options">
  <template #startIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
  <template #startEndIcon="{ size }">
    <dt-icon name="arrow-down" :size="size" />
  </template>
  Place call
</dt-split-button>
</code-example>
<h3 id="custom-end-button-icon" tabindex="-1" class="d-docsite--header-3">Custom End Button Icon <a class="header-anchor d-link d-docsite--link d-link" href="#custom-end-button-icon" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>#endIcon</code> to replace the default chevron icon on the end (omega) button.</p>
<code-example source-code='&lt;dt-split-button importance=&quot;outlined&quot; end-tooltip-text=&quot;More calling options&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  &lt;template #endIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;more-vertical&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
  Place call
&lt;/dt-split-button&gt;'>
<dt-split-button importance="outlined" end-tooltip-text="More calling options">
  <template #startIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
  <template #endIcon="{ size }">
    <dt-icon name="more-vertical" :size="size" />
  </template>
  Place call
</dt-split-button>
</code-example>
<h3 id="icon-only" tabindex="-1" class="d-docsite--header-3">Icon Only <a class="header-anchor d-link d-docsite--link d-link" href="#icon-only" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-split-button end-tooltip-text=&quot;More calling options&quot; start-tooltip-text=&quot;Place call&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;outlined&quot; kind=&quot;muted&quot; end-tooltip-text=&quot;More calling options&quot; start-tooltip-text=&quot;Place call&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button importance=&quot;clear&quot; kind=&quot;critical&quot; end-tooltip-text=&quot;More calling options&quot; start-tooltip-text=&quot;Place call&quot;&gt;
  &lt;template #startIcon=&quot;{ size }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;size&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button end-tooltip-text="More calling options" start-tooltip-text="Place call">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
  </dt-split-button>
  <dt-split-button importance="outlined" kind="muted" end-tooltip-text="More calling options" start-tooltip-text="Place call">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
  </dt-split-button>
  <dt-split-button importance="clear" kind="critical" end-tooltip-text="More calling options" start-tooltip-text="Place call">
    <template #startIcon="{ size }">
      <dt-icon name="phone" :size="size" />
    </template>
  </dt-split-button>
</dt-stack>
</code-example>
<h2 id="leading-trailing" tabindex="-1" class="d-docsite--header-2">Leading &amp; Trailing <a class="header-anchor d-link d-docsite--link d-link" href="#leading-trailing" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>#leading</code> and <code v-pre>#trailing</code> slots are forwarded to the alpha button. Use <code v-pre>alpha-leading-class</code> and <code v-pre>alpha-trailing-class</code> to style the containers.</p>
<code-example source-code='&lt;dt-split-button
  importance=&quot;outlined&quot;
  omega-tooltip-text=&quot;More calling options&quot;
  alpha-trailing-class=&quot;d-pie-100&quot;
&gt;
  Place Call
  &lt;template #trailing&gt;
    &lt;dt-keyboard-shortcut shortcut=&quot;{cmd}+N&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;
&lt;dt-split-button
  importance=&quot;outlined&quot;
  omega-tooltip-text=&quot;More calling options&quot;
  alpha-leading-class=&quot;d-pis-100&quot;
&gt;
  Place Call
  &lt;template #leading&gt;
    &lt;dt-badge kind=&quot;count&quot; text=&quot;3&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-split-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-split-button
    importance="outlined"
    omega-tooltip-text="More calling options"
    alpha-trailing-class="d-pie-100"
  >
    Place Call
    <template #trailing>
      <dt-keyboard-shortcut shortcut="{cmd}+N" />
    </template>
  </dt-split-button>
  <dt-split-button
    importance="outlined"
    omega-tooltip-text="More calling options"
    alpha-leading-class="d-pis-100"
  >
    Place Call
    <template #leading>
      <dt-badge kind="count" text="3" />
    </template>
  </dt-split-button>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="splitButton" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">In addition to the <RouteLink to="/components/button.html#accessibility" class="d-docsite--link d-link">Button component's</RouteLink> accessibility documentation:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">An icon-only primary action and the secondary action require a <RouteLink to="/components/tooltip.html" class="d-docsite--link d-link">Tooltip</RouteLink> to indicate its function.</li>
<li class="d-docsite--list-element">While the secondary action is focused, pressing <code v-pre>Enter</code> or <code v-pre>Space</code> triggers its action, displaying a <RouteLink to="/components/dropdown.html" class="d-docsite--link d-link">Dropdown component</RouteLink> or <RouteLink to="/components/popover.html" class="d-docsite--link d-link">Popover component</RouteLink>.</li>
</ul>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="split-button" /></div></template>


